import React from 'react';
import PropTypes from 'prop-types';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useShopifyCart } from '@web/shopify';
import { RadioImageCard } from '@web/atoms';
import { ApiResult } from '../../api';
import { useReporter } from '../../reporter';
import { useSessionCustomer } from '../../customer';
import { onboardingSubmitted, onboardingBackArrowClicked } from '../events';
import { Question } from '../Question';
import { Footer } from '../Footer';
import { FormFieldsContainer } from '../FormFieldsContainer';
import { StyledForm } from '../StyledForm';
import { schema } from './schema';

export { schema } from './schema';

export const ActivityForm = ({
  activityQuestionPrompt,
  goBack,
  onSubmit,
  levels,
  index,
  defaultValues,
  subheadline,
  ctaButtonText,
}) => {
  const {
    handleSubmit,
    control,
    formState: { isValid, isSubmitting, isSubmitted },
    reset,
    getValues,
  } = useForm({
    mode: 'onChange',
    defaultValues,
    resolver: yupResolver(schema),
  });
  const reporter = useReporter();
  const { customer } = useSessionCustomer();
  const { cart } = useShopifyCart();

  const goBackWithReporter = () => {
    reporter.tag(
      onboardingBackArrowClicked('activity_step', cart?.customer || customer),
    );
    goBack();
  };

  const submitForm = async data => {
    reporter.tag(
      onboardingSubmitted(index, 'dog_activity', cart?.customer || customer),
    );
    const result = await onSubmit(data);
    ApiResult.ifError(result, () =>
      setTimeout(() =>
        reset(getValues(), {
          keepIsSubmitted: false,
        }),
      ),
    );
  };

  return (
    <StyledForm onSubmit={handleSubmit(submitForm)}>
      <FormFieldsContainer>
        <Question prompt={activityQuestionPrompt} subheadline={subheadline}>
          <Controller
            control={control}
            name="activity"
            render={({ field: { ref, ...field } }) => (
              <>
                <RadioImageCard
                  {...field}
                  height={82}
                  width={82}
                  choices={levels}
                  direction="row"
                  name="activity"
                  legendText="activity"
                />
              </>
            )}
          />
        </Question>
      </FormFieldsContainer>
      <Footer
        mobileCTAText={ctaButtonText?.mobileCTA}
        desktopCTAText={ctaButtonText?.desktopCTA}
        isBackButtonDisabled={isSubmitting || isSubmitted}
        isForwardButtonDisabled={!isValid || isSubmitting || isSubmitted}
        onBackButtonClick={goBackWithReporter}
        isForwardButtonLoading={isSubmitting || isSubmitted}
      />
    </StyledForm>
  );
};

ActivityForm.displayName = 'ActivityForm';

ActivityForm.propTypes = {
  activityQuestionPrompt: PropTypes.string.isRequired,
  defaultValues: PropTypes.shape({ activity: PropTypes.string }).isRequired,
  goBack: PropTypes.func.isRequired,
  index: PropTypes.number.isRequired,
  levels: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
      image: PropTypes.object.isRequired,
    }),
  ).isRequired,
  onSubmit: PropTypes.func.isRequired,
  subheadline: PropTypes.string,
  ctaButtonText: PropTypes.object,
};
